import { ReactElement, useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import "./LoginButton.scss";
import {
  IdentityProvider,
  AuthMode,
  EditorStore,
  ViewerStore,
  TokenStore,
} from "../../types";
import { RootState } from "../../reducers";
import { Button, Loader } from "@screencloud/screencloud-ui-components";
import SlackButton from "./slack.png";
import TeamsButton from "./teams.png";
import { getEditorAuth, getViewerAuth } from "../../actions";

const openWindow = (url: string): void => {
  const width = 1024;
  const height = 768;
  // eslint-disable-next-line no-restricted-globals
  const top = screen.height / 2 - height / 2;
  // eslint-disable-next-line no-restricted-globals
  const left = screen.width / 2 - width / 2;
  window.open(
    url,
    "_blank",
    `width=${width},height=${height},top=${top},left=${left}`
  );
};

export enum LoginButtonType {
  Normal = "normal",
  Provider = "provider",
}

interface LoginProps {
  buttonType: LoginButtonType;
  authMode: AuthMode;
  className?: string;
  usageType: "editor" | "viewer";
}

const LoginButton = (props: LoginProps): ReactElement<LoginProps> => {
  const dispatch = useDispatch();
  const editor: EditorStore = useSelector((state: RootState) => state.editor);
  const viewer: ViewerStore = useSelector((state: RootState) => state.viewer);
  const token: TokenStore = useSelector((state: RootState) => state.studio);

  const { authMode, buttonType, className } = props;
  const buttonClass = className || "";
  const [isError, setIsError] = useState(false);

  const slackUrl = (): string =>
    `${process.env.REACT_APP_SLACK_AUTH_URI}?scope=${
      process.env.REACT_APP_SLACK_SCOPES
    }&client_id=${process.env.REACT_APP_SLACK_CLIENT_ID}&redirect_uri=${
      process.env.REACT_APP_REDIRECT_URI
    }&state=${JSON.stringify({
      provider: IdentityProvider.Slack,
    })}`;

  const teamsUrl = (): string =>
    `${process.env.REACT_APP_TEAMS_AUTH_URI}?client_id=${
      process.env.REACT_APP_TEAMS_CLIENT_ID
    }&response_type=code&state=${JSON.stringify({
      provider: IdentityProvider.Teams,
    })}&redirect_uri=${
      process.env.REACT_APP_REDIRECT_URI
    }&prompt=select_account`;

  // below helps resolve an issue where a state update would be attempted
  // on an unmounted component
  const unmounted = useRef(false);

  useEffect(() => {
    return () => {
      unmounted.current = true;
    };
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const { code, provider, closed } = event.data;
      if (event.data.source && event.data.source.includes("react")) {
        return;
      }
      if (closed) {
        return;
      }
      if (!code || !provider) {
        return;
      }

      const { channel } = editor;

      if (channel) {
        const { screenCloudOrgId, spaceId, arn } = channel;
        if (
          provider !== IdentityProvider.Slack &&
          provider !== IdentityProvider.Teams
        ) {
          setIsError(true);
        } else {
          dispatch(
            getEditorAuth({
              provider,
              code,
              orgId: screenCloudOrgId,
              spaceId,
              arn,
            })
          );
        }
      } else {
        const { channel } = viewer;

        if (channel) {
          const { screenCloudOrgId, spaceId, arn } = channel;
          if (
            provider !== IdentityProvider.Slack &&
            provider !== IdentityProvider.Teams
          ) {
            setIsError(true);
          } else {
            dispatch(
              getViewerAuth({
                provider,
                code,
                orgId: screenCloudOrgId,
                spaceId,
                arn,
              })
            );
          }
        }
      }
    };

    window.addEventListener("message", onMessage);

    return () => window.removeEventListener("message", onMessage);
  }, [dispatch, editor, viewer]);

  return (
    <>
      {!token.token || !viewer.token ? (
        <div className="login-container">
          <div className="copy">
            {isError && (
              <div className="error-message">
                There was an error while trying to sign you in.
                <br />
                Please try again.
              </div>
            )}
            <div className="actions">
              {editor.editorAuthenticating ? (
                <>
                  <Loader
                    size="medium"
                    active
                    inline="centered"
                    className="auth-spinner"
                  />
                  <span className="auth-message">Logging you in...</span>
                </>
              ) : (
                <>
                  {authMode === AuthMode.Slack &&
                    (buttonType === LoginButtonType.Provider ? (
                      <span
                        onClick={() => openWindow(slackUrl())}
                        className="sign-in-button"
                      >
                        <img alt="Sign in with Slack" src={SlackButton} />
                      </span>
                    ) : (
                      <Button
                        className={buttonClass}
                        primary
                        onClick={() => openWindow(slackUrl())}
                      >
                        Authenticate Slack Account
                      </Button>
                    ))}
                  {authMode === AuthMode.Teams &&
                    (buttonType === LoginButtonType.Provider ? (
                      <span
                        onClick={() => openWindow(teamsUrl())}
                        className="sign-in-button"
                      >
                        <img alt="Sign in with Microsoft" src={TeamsButton} />
                      </span>
                    ) : (
                      <Button
                        className={buttonClass}
                        primary
                        onClick={() => openWindow(teamsUrl())}
                      >
                        Authenticate Teams Account
                      </Button>
                    ))}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {viewer.user && (
            <>
              You are logged in with {authMode} as {viewer.user.email}
            </>
          )}
        </>
      )}
    </>
  );
};

export default LoginButton;
